import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import StructuredData, { 
  organizationSchema, 
  doctorSchema, 
  medicalBusinessSchema,
  reviewSchema 
} from '@/components/StructuredData';

// Cap a meta description to 165 chars at the last word boundary.
function capDescription(text) {
  if (!text) return '';
  const max = 165;
  if ([...text].length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 80 ? slice.slice(0, lastSpace) : slice).trim();
}

export default function SEO({ 
  title, 
  description, 
  pageType = 'general',
  serviceSchema = null,
  faqSchema = null,
  canonical = null,
  lang = 'en'
}) {
  const location = useLocation();
  const baseUrl = 'https://trinitydentalarts.com';
  const fullUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Set title
    document.title = title;

    // Set or update meta description (capped to 165 chars at the last word boundary)
    const cappedDesc = capDescription(description);
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', cappedDesc);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = cappedDesc;
      document.head.appendChild(metaDescription);
    }

    // Set canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = canonical || fullUrl;
    if (linkCanonical) {
      linkCanonical.setAttribute('href', canonicalUrl);
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      linkCanonical.href = canonicalUrl;
      document.head.appendChild(linkCanonical);
    }

    // Set hreflang tags (currently only en-US, but structure supports expansion)
    let hreflangTag = document.querySelector('link[rel="alternate"][hreflang="en-US"]');
    if (hreflangTag) {
      hreflangTag.setAttribute('href', fullUrl);
    } else {
      hreflangTag = document.createElement('link');
      hreflangTag.rel = 'alternate';
      hreflangTag.hreflang = 'en-US';
      hreflangTag.href = fullUrl;
      document.head.appendChild(hreflangTag);
    }

    // Set x-default hreflang
    let hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (hreflangDefault) {
      hreflangDefault.setAttribute('href', fullUrl);
    } else {
      hreflangDefault = document.createElement('link');
      hreflangDefault.rel = 'alternate';
      hreflangDefault.hreflang = 'x-default';
      hreflangDefault.href = fullUrl;
      document.head.appendChild(hreflangDefault);
    }

    // Set language attribute
    document.documentElement.lang = lang;

    // OG/Twitter description — same concise 165-char cap as the meta description
    const ogDescription = cappedDesc;

    // Open Graph tags
    const ogTags = {
      'og:title': title,
      'og:description': ogDescription,
      'og:url': canonicalUrl,
      'og:type': 'website',
      'og:site_name': 'Trinity Dental Arts',
      'og:locale': 'en_US'
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', content);
      } else {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        metaTag.setAttribute('content', content);
        document.head.appendChild(metaTag);
      }
    });

    // Twitter Card tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': ogDescription
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', content);
      } else {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        metaTag.content = content;
        document.head.appendChild(metaTag);
      }
    });

  }, [title, description, fullUrl, canonical, lang]);

  // Memoize schema generation to avoid regenerating on every render
  const schemas = React.useMemo(() => {
    const result = [];
    
    // Only include essential schemas on critical pages
    if (pageType === 'home') {
      result.push(
        <StructuredData key="org" data={organizationSchema} />,
        <StructuredData key="medical" data={medicalBusinessSchema} />,
        <StructuredData key="doctor" data={doctorSchema} />
      );
    } else if (pageType === 'about' || pageType === 'doctor') {
      result.push(
        <StructuredData key="doctor" data={doctorSchema} />,
        <StructuredData key="org" data={organizationSchema} />
      );
    } else if (pageType === 'service') {
      result.push(
        <StructuredData key="medical" data={medicalBusinessSchema} />,
        serviceSchema && <StructuredData key="service" data={serviceSchema} />
      );
    } else {
      // Minimal schemas for other pages
      result.push(<StructuredData key="org" data={organizationSchema} />);
    }

    // Always include FAQ if provided
    if (faqSchema) {
      result.push(<StructuredData key="faq" data={faqSchema} />);
    }

    return result.filter(Boolean);
  }, [pageType, serviceSchema, faqSchema]);

  return <>{schemas}</>;
}

// Helper function to create service-specific schema
export function createServiceSchema(serviceName, description) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": serviceName,
    "description": description,
    "procedureType": "Dental",
    "bodyLocation": "Mouth",
    "provider": {
      "@type": "Dentist",
      "name": "Trinity Dental Arts",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "11105 Trinity Blvd",
        "addressLocality": "Trinity",
        "addressRegion": "FL",
        "postalCode": "34655"
      },
      "telephone": "+1-727-228-6846"
    }
  };
}

// Helper function to create FAQ schema
export function createFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question || faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer || faq.a
      }
    }))
  };
}